/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.viewmodel

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import systems.frictionless.impulse.model.AuthenticatedUser
import systems.frictionless.impulse.rest.client.RestClient
import systems.frictionless.impulse.rest.client.model.Authentication
import systems.frictionless.impulse.rest.client.model.RestResponse
import systems.frictionless.impulse.rest.model.Email
import systems.frictionless.impulse.rest.model.Login
import systems.frictionless.impulse.rest.model.User

/*
 * Note we differentiate between three words:
 *  - Login is the entire process of getting the user authenticated, which includes sign up and sign in.
 *  - Sign up is creating a new account
 *  - Sign in is accessing an existing account by upgrading from basic authentication to token authentication
 *
 * The actual flow is:
 *  1. Check whether the user has an account
 *  2. Create a new account (can be skipped if the user has an account)
 *  3. Get session token
 *  4. Get user object
 */
class LoginViewModel(private val restClient: RestClient) {
    private val mutableState = MutableStateFlow<State>(State.Check(restClient))

    val state: StateFlow<State> = mutableState

    // There is room to automate this method, so that the UI doesn't have to check before making state transitions
    fun transition() {
        when (val loginStateSnapshot = state.value) {
            is State.Check -> {
                val checkStateSnapshot = loginStateSnapshot.state.value
                if (checkStateSnapshot is NetworkViewModelState.Complete) {
                    when (checkStateSnapshot.result.login.result) {
                        Login.Result.SignUp ->
                            mutableState.tryEmit(
                                State.SignUp(restClient)
                            )
                        Login.Result.SignIn ->
                            mutableState.tryEmit(
                                State.SignIn(restClient)
                            )
                    }
                }
            }

            is State.SignUp -> {
                val signupStateSnapshot = loginStateSnapshot.state.value
                if (signupStateSnapshot is NetworkViewModelState.Complete) {
                    mutableState.tryEmit(State.SignIn(restClient))
                }
            }

            is State.SignIn -> {
                val signInStateSnapshot = loginStateSnapshot.state.value
                if (signInStateSnapshot is NetworkViewModelState.Complete) {
                    mutableState.tryEmit(State.GetUser(restClient))
                }
            }
            is State.GetUser -> {
                // Do nothing; cannot transition further
            }
        }
    }

    sealed class State {
        class Check(private val restClient: RestClient) : State() {
            data class EmailWithLogin(val email: Email, val login: Login)

            private val mutableState =
                MutableStateFlow<NetworkViewModelState<Login.Builder, Login, EmailWithLogin>>(NetworkViewModelState.Idle())

            val state: StateFlow<NetworkViewModelState<Login.Builder, Login, EmailWithLogin>> = mutableState

            /**
             * @param inScope Scope to use for requests to the server.
             * @param loginBuilder Login to check for.
             * Note: [state] is expected to be Idle when when this method is called.
             */
            fun startLoginCheck(
                inScope: CoroutineScope,
                loginBuilder: Login.Builder
            ) {
                check(state.value is NetworkViewModelState.Idle || state.value is NetworkViewModelState.Failure)
                mutableState.tryEmit(NetworkViewModelState.Busy())

                inScope.launch {
                    val newState: NetworkViewModelState<Login.Builder, Login, EmailWithLogin> =
                        when (val restResponse = restClient.login(loginBuilder)) {
                            is RestResponse.Success ->
                                NetworkViewModelState.Complete(
                                    EmailWithLogin(
                                        loginBuilder.email,
                                        restResponse.result
                                    )
                                )
                            is RestResponse.Failure -> NetworkViewModelState.Failure(loginBuilder, restResponse)
                        }

                    mutableState.tryEmit(newState)
                }
            }
        }

        class SignUp(private val restClient: RestClient) : State() {
            private val mutableState =
                MutableStateFlow<NetworkViewModelState<User.Builder, User, Authentication.Form>>(NetworkViewModelState.Idle())

            val state: StateFlow<NetworkViewModelState<User.Builder, User, Authentication.Form>> = mutableState

            /**
             * @param inScope Scope to use for requests to the server.
             * @param userBuilder User to create.
             * Note: [state] is expected to be Idle when when this method is called.
             */
            fun startSignUp(
                inScope: CoroutineScope,
                userBuilder: User.Builder
            ) {
                check(state.value is NetworkViewModelState.Idle || state.value is NetworkViewModelState.Failure)
                mutableState.tryEmit(NetworkViewModelState.Busy())

                inScope.launch {
                    val newState: NetworkViewModelState<User.Builder, User, Authentication.Form> =
                        when (val restResponse = restClient.createUser(userBuilder)) {
                            is RestResponse.Success -> {
                                NetworkViewModelState.Complete(
                                    Authentication.Form(
                                        restResponse.result.email,
                                        userBuilder.password.plaintext
                                    )
                                )
                            }
                            is RestResponse.Failure -> NetworkViewModelState.Failure(userBuilder, restResponse)
                        }

                    mutableState.tryEmit(newState)
                }
            }
        }

        class SignIn(private val restClient: RestClient) : State() {
            private val mutableState =
                MutableStateFlow<NetworkViewModelState<Authentication.Form, Authentication.Bearer, Authentication.Bearer>>(
                    NetworkViewModelState.Idle()
                )

            val state: StateFlow<NetworkViewModelState<Authentication.Form, Authentication.Bearer, Authentication.Bearer>> =
                mutableState

            /**
             * @param inScope Scope to use for requests to the server.
             * @param formAuthBuilder Basic auth to upgrade to token authentication.
             * Note: [state] is expected to be Idle when when this method is called.
             */
            fun startSignIn(
                inScope: CoroutineScope,
                formAuthBuilder: Authentication.Form
            ) {
                val value = state.value
                check(value is NetworkViewModelState.Idle || value is NetworkViewModelState.Failure) {
                    "state.value is $value"
                }
                mutableState.tryEmit(NetworkViewModelState.Busy())

                inScope.launch {
                    val newState: NetworkViewModelState<Authentication.Form, Authentication.Bearer, Authentication.Bearer> =
                        when (val restResponse = restClient.createSession(formAuthBuilder)) {
                            is RestResponse.Success -> {
                                NetworkViewModelState.Complete(restResponse.result)
                            }
                            is RestResponse.Failure -> NetworkViewModelState.Failure(formAuthBuilder, restResponse)
                        }

                    mutableState.tryEmit(newState)
                }
            }
        }

        class GetUser(private val restClient: RestClient) : State() {
            private val mutableState =
                MutableStateFlow<NetworkViewModelState<Authentication.Bearer, User, AuthenticatedUser>>(
                    NetworkViewModelState.Idle()
                )

            val state: StateFlow<NetworkViewModelState<Authentication.Bearer, User, AuthenticatedUser>> =
                mutableState

            /**
             * @param inScope Scope to use for requests to the server.
             * @param bearerAuthBuilder Bearer auth to get the user.
             * Note: [state] is expected to be Idle when when this method is called.
             */
            fun startGetUser(
                inScope: CoroutineScope,
                bearerAuthBuilder: Authentication.Bearer
            ) {
                check(state.value is NetworkViewModelState.Idle || state.value is NetworkViewModelState.Failure)
                mutableState.tryEmit(NetworkViewModelState.Busy())

                inScope.launch {
                    val newState: NetworkViewModelState<Authentication.Bearer, User, AuthenticatedUser> =
                        when (val restResponse = restClient.getUser(bearerAuthBuilder)) {
                            is RestResponse.Success ->
                                NetworkViewModelState.Complete(
                                    AuthenticatedUser(
                                        restResponse.result,
                                        bearerAuthBuilder
                                    )
                                )
                            is RestResponse.Failure -> NetworkViewModelState.Failure(bearerAuthBuilder, restResponse)
                        }

                    mutableState.tryEmit(newState)
                }
            }
        }
    }
}
